import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEntries } from "../hooks/useEntries";
import { useNavigate } from "react-router-dom";
import { t } from "../i18n";
import { usePendientes } from "../hooks/usePendientes";
import { useInvestments } from "../hooks/useInvestments";
import { formatAmount, getCurrencySymbol } from "../config/currencies";
// (sin paleta de colores manual aquí: los donuts de categoría generan
// su color automáticamente a partir del tema, ver más abajo)
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList
} from "recharts";
import { BarChart3, Lock, TrendingUp, LineChart as LineChartIcon } from "lucide-react";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
// fmt y fmtTip se definen dentro del componente porque dependen de la moneda activa

// Lee las variables CSS del tema activo
function getThemeColors() {
  const s = getComputedStyle(document.documentElement);
  const get = v => s.getPropertyValue(v).trim();
  return {
    income:   get("--green")   || "#16a34a",
    expense:  get("--red")     || "#dc2626",
    balance:  get("--blue")    || "#2563eb",
    fund:     get("--purple")  || "#7c3aed",
    amber:    get("--amber")   || "#d97706",
    grid:     get("--gray-100")|| "#f3f4f6",
    tooltip:  get("--card")    || "#ffffff",
    c1:       get("--chart-1") || "#2563eb",
    c2:       get("--chart-2") || "#16a34a",
    c3:       get("--chart-3") || "#d97706",
    c4:       get("--chart-4") || "#7c3aed",
    c5:       get("--chart-5") || "#dc2626",
  };
}

// Misma lógica que en Panel.js — una "operación combinada" guarda el
// NETO en amount/type, y el bruto de cada lado en incomeAmount/expenseAmount.
// Sumar solo por type/amount perdía el lado gasto de cualquier combinada
// con neto positivo (y viceversa), afectando gráficos y desgloses por categoría.
function getIncomePart(e) {
  if (e.isCombined) return e.incomeAmount || 0;
  return e.type === "income" ? (e.amount || 0) : 0;
}
function getExpensePart(e) {
  if (e.isCombined) return e.expenseAmount || 0;
  return e.type === "expense" ? (e.amount || 0) : 0;
}

export default function Graficos() {
  const { profile, lang, theme, currency } = useAuth();
  const { visibleEntries, loading, isPro } = useEntries();
  const navigate = useNavigate();
  const { pendientes, getRestante } = usePendientes();
  const { investments } = useInvestments();
  const c   = profile?.config || {};
  const pct = c.fund_pct || 10;
  const T = (key) => t(lang, key);
  const fmt = n => getCurrencySymbol(currency) + Number(n).toLocaleString("es-ES",{minimumFractionDigits:0,maximumFractionDigits:0});
  const fmtTip = n => formatAmount(n, currency);
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterMonth, setFilterMonth] = useState("Todos");
  // Rango del gráfico "Inversión por mes" — en meses (3, 12, 60) o
  // Infinity para "todo". Solo se aplica cuando no hay un mes/año concreto
  // seleccionado arriba (si hay filtro, ese manda).
  const [investChartRange, setInvestChartRange] = useState(12);

  // Popover de detalle: se muestra al clicar un punto de un gráfico,
  // con el importe/categoría y un botón para ir al historial filtrado.
  // { x, y, title, value, color, filter } | null
  const [popover, setPopover] = useState(null);

  // Colores reactivos: se recalculan cada vez que cambia el tema
  const [clr, setClr] = useState(getThemeColors);
  useEffect(() => { setClr(getThemeColors()); }, [theme]);

  // (El array de colores arcoíris por categoría se sustituyó por una
  // escala monocromática del acento del tema; ver expPieColors/incPieColors)

  const years = useMemo(() => {
    const ys = [...new Set(visibleEntries.map(e=>e.year))].sort();
    return ys.length ? ys : [new Date().getFullYear()];
  },[visibleEntries]);

  const data = useMemo(() => visibleEntries.filter(e => {
    const yearOk  = filterYear==="Todos"  || e.year===parseInt(filterYear);
    const monthOk = filterMonth==="Todos" || e.month===filterMonth;
    return yearOk && monthOk;
  }),[visibleEntries,filterYear,filterMonth]);

  const barData = useMemo(() => {
    const keys = [...new Set(data.map(e=>`${e.year}-${e.month}`))];
    return keys.sort((a,b)=>{
      const [ay,am]=a.split("-"); const [by,bm]=b.split("-");
      return ay!==by?ay-by:MONTHS.indexOf(am)-MONTHS.indexOf(bm);
    }).map(k=>{
      const [y,m] = k.split("-");
      const rows  = data.filter(e=>String(e.year)===y&&e.month===m);
      const inc   = rows.reduce((s,e)=>s+getIncomePart(e),0);
      const exp   = rows.reduce((s,e)=>s+getExpensePart(e),0);
      const bal   = inc-exp;
      return { label:`${m.slice(0,3)} ${String(y).slice(2)}`, year:parseInt(y), month:m, inc, exp, bal, inv:Math.max(0,bal*(pct/100)) };
    });
  },[data,pct]);

  const expPie = useMemo(() => {
    const by = {};
    data.forEach(e=>{ const part = getExpensePart(e); if (part > 0) by[e.cat||"Otro"]=(by[e.cat||"Otro"]||0)+part; });
    return Object.entries(by).sort((a,b)=>b[1]-a[1]).map(([name,value])=>({name,value}));
  },[data]);

  const incPie = useMemo(() => {
    const by = {};
    data.forEach(e=>{ const part = getIncomePart(e); if (part > 0) by[e.cat||"Otro"]=(by[e.cat||"Otro"]||0)+part; });
    return Object.entries(by).sort((a,b)=>b[1]-a[1]).map(([name,value])=>({name,value}));
  },[data]);

  // Color automático de cada porción del donut: tonos derivados del propio
  // --green/--red del tema (clr.income/clr.expense), igual que las barras
  // de "Ingresos vs Gastos" y "Resumen de pendientes" — sin paleta manual
  // de por medio, todo se deriva del tema en sí.
  function tintTowardsWhite(hex, factor) {
    const clean = hex.replace("#", "");
    const num = parseInt(clean, 16);
    const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
    const mix = (c) => Math.round(c + (255 - c) * factor);
    return `#${[mix(r), mix(g), mix(b)].map(v => v.toString(16).padStart(2, "0")).join("")}`;
  }
  function autoScale(baseHex, count) {
    if (count <= 1) return [baseHex];
    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(tintTowardsWhite(baseHex, (i / (count - 1)) * 0.7));
    }
    return colors;
  }
  const expPieColors = useMemo(() => autoScale(clr.income, Math.max(1, expPie.length)), [clr.income, expPie.length]);
  const incPieColors = useMemo(() => autoScale(clr.income, Math.max(1, incPie.length)), [clr.income, incPie.length]);

  // Distribución de inversiones por activo (igual que "Totales por activo"
  // en Inversiones.js): agrupa por la Nota (ej. "cripto", "Nvidia"), suma
  // el neto invertido de cada uno, y calcula qué % del total representa —
  // solo se cuentan grupos con neto positivo (los que ya se retiraron del
  // todo no aportan nada a la distribución actual).
  const UNSPECIFIED_LABEL = lang === "en" ? "Unspecified" : "Sin especificar";
  const investmentGroups = useMemo(() => {
    const groups = {};
    investments
      .filter(i => !i.migrated && (i.symbol || i.note || i.platform))
      .forEach(i => {
        // OJO: nunca se usa "platform" como nombre del activo — la
        // plataforma (Revolut, Binance...) es DÓNDE se invirtió, no QUÉ se
        // compró. Las que solo tienen plataforma (sin símbolo ni nota) se
        // agrupan aparte en "Sin especificar", para poder localizarlas.
        const displayName = i.symbol || i.note ? (i.symbol || i.note).trim() : UNSPECIFIED_LABEL;
        const key = displayName.trim().toLowerCase();
        if (!groups[key]) groups[key] = { name: displayName.trim(), value: 0 };
        groups[key].value += i.amount || 0;
      });
    return Object.values(groups).filter(g => g.value > 0).sort((a,b) => b.value - a.value);
  }, [investments]);
  const investmentColors = useMemo(() => autoScale(clr.fund, Math.max(1, investmentGroups.length)), [clr.fund, investmentGroups.length]);

  // Inversión por mes (para el gráfico de barras "qué mes se invirtió
  // más") — respeta el mismo filtro de año/mes de arriba de la página,
  // igual que el resto de gráficos.
  const investMonthlyData = useMemo(() => {
    const byMonth = {};
    investments
      .filter(i => !i.migrated)
      .filter(i => {
        const yearOk  = filterYear==="Todos"  || i.year===parseInt(filterYear);
        const monthOk = filterMonth==="Todos" || i.month===filterMonth;
        return yearOk && monthOk;
      })
      .forEach(i => {
        const key = `${i.year}-${i.month}`;
        if (!byMonth[key]) byMonth[key] = { year: i.year, month: i.month, total: 0 };
        byMonth[key].total += i.amount || 0;
      });
    return Object.values(byMonth)
      .sort((a,b) => a.year!==b.year ? a.year-b.year : MONTHS.indexOf(a.month)-MONTHS.indexOf(b.month))
      .map(m => ({ label:`${m.month.slice(0,3)} ${String(m.year).slice(2)}`, year:m.year, month:m.month, total:m.total }));
  }, [investments, filterYear, filterMonth]);


  const CustomTooltip = ({active,payload,label}) => {
    if (!active||!payload?.length) return null;
    return (
      <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:8,padding:"10px 14px",fontSize:13,boxShadow:"var(--shadow-md)",color:"var(--gray-900)"}}>
        <p style={{fontWeight:600,marginBottom:6,color:"var(--gray-900)"}}>{label}</p>
        {payload.map((p,i)=>(
          <p key={i} style={{color:p.color}}>{p.name}: {fmtTip(p.value)}</p>
        ))}
      </div>
    );
  };

  if (loading) return <div className="page"><p style={{color:"var(--gray-400)"}}>Cargando...</p></div>;

  // Navega a la pantalla indicada aplicando el filtro correspondiente al punto clicado.
  // path por defecto es /historial; el pie de pendientes usa /pendientes.
  function goToFiltered(filter = {}, path = "/historial") {
    const params = new URLSearchParams();
    if (filter.asset) {
      // Para Inversiones por activo, no arrastramos el año/mes del propio
      // gráfico — queremos ver TODO el histórico de ese activo, no solo el
      // periodo que estuviera seleccionado aquí.
      params.set("asset", filter.asset);
      setPopover(null);
      navigate(`${path}?${params.toString()}`);
      return;
    }
    // Usar el año/mes del filtro del gráfico si no vienen explícitos en filter
    const yr = filter.year  || (filterYear  !== "Todos" ? filterYear  : null);
    const mo = filter.month || (filterMonth !== "Todos" ? filterMonth : null);
    if (yr) params.set("year", yr);
    if (mo) params.set("month", mo);
    if (filter.type)  params.set("type", filter.type);
    if (filter.cat)   params.set("cat", filter.cat);
    setPopover(null);
    navigate(`${path}?${params.toString()}`);
  }

  // Abre el popover de detalle junto al punto clicado en un gráfico.
  // clientX/clientY vienen del evento de mouse nativo (event.nativeEvent o event)
  function showPopover(clientX, clientY, { title, value, color, filter, path = "/historial" }) {
    setPopover({ x: clientX, y: clientY, title, value, color, filter, path });
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}><LineChartIcon size={22}/> {T("charts_title")}</h2>
          <p className="page-sub">{T("charts_sub")}</p>
        </div>
        <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
          <select className="filter-select" value={filterYear} onChange={e=>{ setFilterYear(e.target.value); setFilterMonth("Todos"); }}>
            <option value="Todos">{T("charts_all_years")}</option>
            {years.map(y=><option key={y}>{y}</option>)}
          </select>
          <select className="filter-select" value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}>
            <option value="Todos">{lang==="en"?"All months":"Todos los meses"}</option>
            {MONTHS.map(m=><option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {!isPro && (
        <div className="pro-banner" style={{marginBottom:24}}>
          <div>
            <h3>{T("charts_pro_title")}</h3>
            <p>{T("charts_pro_sub")}</p>
          </div>
          <button className="btn btn-pro btn-sm" onClick={()=>navigate("/pro")}>{T("charts_pro_btn")}</button>
        </div>
      )}

      {barData.length === 0 ? (
        <div className="empty"><div className="empty-icon"><BarChart3 size={28}/></div><h3>{T("charts_no_data")}</h3><p>{T("charts_no_data_sub")}</p></div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,360px),1fr))",gap:20}}>

          <div className="card" style={{gridColumn:"1/-1"}}>
            <h3 style={{fontSize:15,fontWeight:600,marginBottom:16}}>{c.income_label} {T("charts_bar_title")} {c.expense_label} {T("charts_bar_title2")}</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={barData} margin={{top:0,right:10,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke={clr.grid} />
                <XAxis dataKey="label" tick={{fontSize:11,fill:"var(--gray-500)"}} />
                <YAxis tickFormatter={fmt} tick={{fontSize:11,fill:"var(--gray-500)"}} width={60} />
                <Tooltip content={<CustomTooltip/>} cursor={false} />
                <Legend wrapperStyle={{fontSize:13}} />
                <Bar dataKey="inc" name={c.income_label||T("income_label")} fill={clr.income} radius={[4,4,0,0]} cursor="pointer"
                     onClick={(data,index,event)=>showPopover(event.clientX, event.clientY, {
                       title: `${c.income_label||T("income_label")} · ${data.label}`,
                       value: fmtTip(data.inc), color: clr.income,
                       filter: { year: data.year, month: data.month, type: "income" }
                     })} />
                <Bar dataKey="exp" name={c.expense_label||T("expense_label")} fill={clr.expense} radius={[4,4,0,0]} cursor="pointer"
                     onClick={(data,index,event)=>showPopover(event.clientX, event.clientY, {
                       title: `${c.expense_label||T("expense_label")} · ${data.label}`,
                       value: fmtTip(data.exp), color: clr.expense,
                       filter: { year: data.year, month: data.month, type: "expense" }
                     })} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{position:"relative",gridColumn:isPro?"auto":"1/-1"}}>
            <h3 style={{fontSize:15,fontWeight:600,marginBottom:16}}>{T("charts_line_title")} {c.balance_label?.toLowerCase()||T("balance_label").toLowerCase()}</h3>
            {!isPro && (
              <div style={{position:"absolute",inset:0,background:"var(--card)",opacity:.9,borderRadius:"var(--radius)",
                           display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:2,gap:8}}>
                <span style={{display:"flex"}}><Lock size={26}/></span>
                <p style={{fontSize:14,fontWeight:600,color:"var(--gray-700)"}}>{T("charts_locked")}</p>
                <button className="btn btn-pro btn-sm" onClick={()=>navigate("/pro")}>{T("charts_unlock")}</button>
              </div>
            )}
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={barData} margin={{top:0,right:10,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke={clr.grid} />
                <XAxis dataKey="label" tick={{fontSize:11,fill:"var(--gray-500)"}} />
                <YAxis tickFormatter={fmt} tick={{fontSize:11,fill:"var(--gray-500)"}} width={60} />
                <Tooltip content={<CustomTooltip/>} cursor={false} />
                <Legend wrapperStyle={{fontSize:13}} />
                <Line dataKey="bal" name={c.balance_label||T("balance_label")} stroke={clr.balance} strokeWidth={2.5}
                      dot={{r:4,fill:clr.balance,cursor:"pointer"}}
                      activeDot={{r:6,cursor:"pointer",onClick:(event,payload)=>{
                        const d = payload?.payload || {};
                        showPopover(event.clientX, event.clientY, {
                          title: `${c.balance_label||T("balance_label")} · ${d.label}`,
                          value: fmtTip(d.bal), color: clr.balance,
                          filter: { year: d.year, month: d.month }
                        });
                      }}} />
                <Line dataKey="inv" name={`${c.fund_label||T("fund_label")} (${pct}%)`} stroke={clr.fund} strokeWidth={2} strokeDasharray="5 3"
                      dot={{r:3,fill:clr.fund,cursor:"pointer"}}
                      activeDot={{r:5,cursor:"pointer",onClick:(event,payload)=>{
                        const d = payload?.payload || {};
                        showPopover(event.clientX, event.clientY, {
                          title: `${c.fund_label||T("fund_label")} · ${d.label}`,
                          value: fmtTip(d.inv), color: clr.fund,
                          filter: { year: d.year, month: d.month }
                        });
                      }}} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {isPro && (
            <div className="card">
              <h3 style={{fontSize:15,fontWeight:600,marginBottom:16}}>{c.expense_label} {T("charts_exp_pie")}</h3>
              {expPie.length===0 ? <p style={{color:"var(--gray-400)",fontSize:13}}>{T("charts_no_data_pie")}</p> : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={expPie} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value">
                        {expPie.map((entry,i)=>(
                          <Cell key={i} fill={expPieColors[i]} cursor="pointer"
                                onClick={(e)=>showPopover(e.clientX, e.clientY, {
                                  title: entry.name,
                                  value: fmtTip(entry.value), color: expPieColors[i],
                                  filter: { type: "expense", cat: entry.name }
                                })} />
                        ))}
                      </Pie>
                      <Tooltip formatter={v=>fmtTip(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Leyenda propia debajo del donut: con muchas categorías,
                      Recharts <Legend> se monta encima del gráfico porque
                      comparte su altura fija. Esta leyenda en HTML normal
                      siempre queda debajo, creciendo en su propia cuadrícula. */}
                  <div style={{display:"flex",flexWrap:"wrap",gap:"6px 14px",justifyContent:"center",marginTop:12}}>
                    {expPie.map((entry,i)=>(
                      <span key={i} style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:"var(--gray-600)"}}>
                        <span style={{width:9,height:9,borderRadius:2,background:expPieColors[i],flexShrink:0}}/>
                        {entry.name}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {isPro && (
            <div className="card">
              <h3 style={{fontSize:15,fontWeight:600,marginBottom:16}}>{c.income_label} {T("charts_inc_pie")}</h3>
              {incPie.length===0 ? <p style={{color:"var(--gray-400)",fontSize:13}}>{T("charts_no_data_pie")}</p> : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={incPie} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value">
                        {incPie.map((entry,i)=>(
                          <Cell key={i} fill={incPieColors[i]} cursor="pointer"
                                onClick={(e)=>showPopover(e.clientX, e.clientY, {
                                  title: entry.name,
                                  value: fmtTip(entry.value), color: incPieColors[i],
                                  filter: { type: "income", cat: entry.name }
                                })} />
                        ))}
                      </Pie>
                      <Tooltip formatter={v=>fmtTip(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{display:"flex",flexWrap:"wrap",gap:"6px 14px",justifyContent:"center",marginTop:12}}>
                    {incPie.map((entry,i)=>(
                      <span key={i} style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:"var(--gray-600)"}}>
                        <span style={{width:9,height:9,borderRadius:2,background:incPieColors[i],flexShrink:0}}/>
                        {entry.name}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {investmentGroups.length > 0 && (
            <div className="card" style={{gridColumn:"1/-1"}}>
              <h3 style={{fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>
                <TrendingUp size={16}/> {lang==="en" ? "Investments by asset" : "Inversiones por activo"}
              </h3>
              <div style={{display:"flex",alignItems:"center",gap:32,flexWrap:"wrap"}}>
                <ResponsiveContainer width={180} height={180}>
                  <PieChart>
                    <Pie data={investmentGroups} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                      {investmentGroups.map((entry,i)=>(
                        <Cell key={i} fill={investmentColors[i]} cursor="pointer"
                              onClick={(e)=>showPopover(e.clientX, e.clientY, {
                                title: entry.name,
                                value: fmtTip(entry.value), color: investmentColors[i],
                                filter: { asset: entry.name }, path: "/inversiones"
                              })} />
                      ))}
                    </Pie>
                    <Tooltip formatter={v=>fmtTip(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{display:"flex",flexDirection:"column",gap:10,flex:1,minWidth:180}}>
                  {investmentGroups.map((g,i) => {
                    const total = investmentGroups.reduce((s,x)=>s+x.value,0);
                    const p = total>0 ? Math.round((g.value/total)*100) : 0;
                    return (
                      <div key={i} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}
                           onClick={()=>goToFiltered({ asset: g.name }, "/inversiones")}>
                        <span style={{width:10,height:10,borderRadius:2,background:investmentColors[i],flexShrink:0}}/>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--gray-700)",
                                       whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{g.name}</div>
                        </div>
                        <div style={{fontSize:13,fontWeight:700,color:investmentColors[i]}}>{p}%</div>
                        <div style={{fontSize:12,color:"var(--gray-400)",minWidth:70,textAlign:"right"}}>{fmtTip(g.value)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {investMonthlyData.length > 0 && (() => {
            // Si no hay un año concreto seleccionado arriba (filterYear
            // "Todos"), se limita al rango elegido con los botones de
            // periodo (3M/1A/5A), para que no se sature con el tiempo.
            // investMonthlyData ya viene ordenado de más antiguo a más
            // reciente, así que los últimos N son los más recientes.
            const sliced = filterYear === "Todos"
              ? investMonthlyData.slice(investChartRange === Infinity ? 0 : -investChartRange)
              : investMonthlyData;
            // Los botones de periodo solo se muestran si de verdad cambian
            // algo — con 3 meses de datos o menos, "3M/1A/5A" darían
            // siempre el mismo resultado.
            const showRangeButtons = filterYear === "Todos" && investMonthlyData.length > 3;

            const chartViewW = 480, chartH = 110;
            const nPoints = Math.max(sliced.length, 1);
            const slot = chartViewW / nPoints;
            // Escala ajustada al rango real de los valores (como hace
            // CoinGecko) — así variaciones pequeñas entre meses parecidos
            // se ven, en vez de aplastarse todas arriba con hueco vacío
            // debajo. Se deja un margen del 15% arriba y abajo.
            const rawMax = Math.max(...sliced.map(m => m.total), 0);
            const rawMin = Math.min(...sliced.map(m => m.total), rawMax);
            const pad = Math.max((rawMax - rawMin) * 0.15, rawMax * 0.05, 1);
            const scaleMax = rawMax + pad;
            const scaleMin = Math.max(0, rawMin - pad);
            const range = scaleMax - scaleMin || 1;
            // Margen fijo arriba (etiqueta del importe) y abajo (etiqueta
            // del mes) — sin esto, los puntos más altos recortaban su
            // etiqueta contra el borde superior del lienzo.
            const topPad = 20, bottomPad = 20;
            const points = sliced.map((m, i) => ({
              x: i * slot + slot / 2,
              y: topPad + chartH - ((m.total - scaleMin) / range) * chartH,
              m,
            }));
            // Curva suave (Catmull-Rom a Bézier) en vez de líneas rectas —
            // igual de fiel a los datos, pero más vistosa.
            function smoothPath(pts) {
              if (pts.length < 2) return "";
              let d = `M ${pts[0].x},${pts[0].y}`;
              for (let i = 0; i < pts.length - 1; i++) {
                const p0 = pts[i === 0 ? 0 : i - 1], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2 < pts.length ? i + 2 : i + 1];
                const cp1x = p1.x + (p2.x - p0.x) / 6, cp1y = p1.y + (p2.y - p0.y) / 6;
                const cp2x = p2.x - (p3.x - p1.x) / 6, cp2y = p2.y - (p3.y - p1.y) / 6;
                d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
              }
              return d;
            }
            const linePath = smoothPath(points);
            const baseline = topPad + chartH;
            const areaPath = points.length > 1
              ? `${linePath} L ${points[points.length-1].x},${baseline} L ${points[0].x},${baseline} Z`
              : "";
            const trendUp = points.length > 1 ? points[points.length-1].m.total >= points[0].m.total : true;
            const trendColor = trendUp ? "var(--green,#1B7F3A)" : "var(--red,#C0392B)";
            return (
              <div className="card" style={{gridColumn:"1/-1"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10, marginBottom:14}}>
                  <h3 style={{fontSize:15,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                    <BarChart3 size={16}/> {lang==="en" ? "Investment by month" : "Inversión por mes"}
                  </h3>
                  {showRangeButtons && (
                    <div style={{display:"flex", gap:6}}>
                      {[[3,"3M"],[12,lang==="en"?"1Y":"1A"],[Infinity,lang==="en"?"5Y":"5A"]].map(([val,label])=>(
                        <button key={label} type="button" onClick={()=>setInvestChartRange(val)}
                                style={{padding:"4px 11px", borderRadius:14, fontSize:11, fontWeight:600, cursor:"pointer",
                                        border:`1.5px solid ${investChartRange===val?clr.fund:"var(--border)"}`,
                                        background: investChartRange===val ? clr.fund : "white",
                                        color: investChartRange===val ? "white" : "var(--gray-600)"}}>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {points.length < 2 ? (
                  <p style={{fontSize:13, color:"var(--gray-400)"}}>
                    {lang==="en" ? "Not enough months in this range to compare." : "No hay suficientes meses en este rango para comparar."}
                  </p>
                ) : (
                  <svg viewBox={`0 0 ${chartViewW} ${topPad + chartH + bottomPad}`} style={{width:"100%", height:"auto", display:"block", maxHeight:200}}>
                    <defs>
                      <linearGradient id="investLineGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={trendColor} stopOpacity={0.25}/>
                        <stop offset="100%" stopColor={trendColor} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <path d={areaPath} fill="url(#investLineGrad)" />
                    <path d={linePath} fill="none" stroke={trendColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    {points.map((p, i) => (
                      <g key={i} style={{cursor:"pointer"}}
                         onClick={(e)=>showPopover(e.clientX, e.clientY, {
                           title: `${lang==="en"?"Invested":"Invertido"} · ${p.m.label}`,
                           value: fmtTip(p.m.total), color: clr.fund,
                           filter: { year: String(p.m.year), month: p.m.month }, path: "/inversiones"
                         })}>
                        {(i === 0 || i === points.length - 1 || points.length <= 6) && (
                          <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="10" fill="var(--gray-600)" fontWeight="600">
                            {fmt(p.m.total)}
                          </text>
                        )}
                        <circle cx={p.x} cy={p.y} r="4" fill="#fff" stroke={trendColor} strokeWidth="2.5" />
                        <text x={p.x} y={baseline + 16} textAnchor="middle" fontSize="10" fill="var(--gray-500)">
                          {p.m.label}
                        </text>
                      </g>
                    ))}
                  </svg>
                )}
              </div>
            );
          })()}

          <div className="card" style={{gridColumn:"1/-1"}}>
            <h3 style={{fontSize:15,fontWeight:600,marginBottom:16}}>
              ⏳ {lang==="en"?"Pending overview":"Resumen de pendientes"}
            </h3>
            {pendientes.length === 0 ? (
              <p style={{color:"var(--gray-400)",fontSize:13,textAlign:"center",padding:"20px 0"}}>
                {lang==="en"?"No pending entries":"Sin pendientes"}
              </p>
            ) : (() => {
              const totalPorCobrar = pendientes.filter(p=>p.type==="income").reduce((s,p)=>s+getRestante(p),0);
              const totalPorPagar  = pendientes.filter(p=>p.type==="expense").reduce((s,p)=>s+getRestante(p),0);
              return (
              <div style={{display:"flex",alignItems:"center",gap:32,flexWrap:"wrap"}}>
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie
                      data={[
                        {name:lang==="en"?"To collect":"Por cobrar", value:totalPorCobrar},
                        {name:lang==="en"?"To pay":"Por pagar", value:totalPorPagar},
                      ]}
                      cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                      <Cell fill={clr.income} cursor="pointer"
                            onClick={(e)=>showPopover(e.clientX, e.clientY, {
                              title: lang==="en"?"To collect":"Por cobrar",
                              value: fmtTip(totalPorCobrar), color: clr.income,
                              filter: { type: "income" }, path: "/pendientes"
                            })} />
                      <Cell fill={clr.expense} cursor="pointer"
                            onClick={(e)=>showPopover(e.clientX, e.clientY, {
                              title: lang==="en"?"To pay":"Por pagar",
                              value: fmtTip(totalPorPagar), color: clr.expense,
                              filter: { type: "expense" }, path: "/pendientes"
                            })} />
                    </Pie>
                    <Tooltip formatter={v=>fmtTip(v)}/>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  {[
                    {label:lang==="en"?"To collect":"Por cobrar", value:totalPorCobrar, color:clr.income},
                    {label:lang==="en"?"To pay":"Por pagar", value:totalPorPagar, color:clr.expense},
                  ].map(item=>(
                    <div key={item.label} style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{width:10,height:10,borderRadius:2,background:item.color,flexShrink:0}}></span>
                      <div>
                        <div style={{fontSize:12,color:"var(--gray-500)"}}>{item.label}</div>
                        <div style={{fontSize:18,fontWeight:600,color:item.color}}>{fmtTip(item.value)}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{width:10,height:10,borderRadius:2,background:clr.balance,flexShrink:0}}></span>
                    <div>
                      <div style={{fontSize:12,color:"var(--gray-500)"}}>{lang==="en"?"Net balance":"Balance neto"}</div>
                      <div style={{fontSize:18,fontWeight:600,color: (totalPorCobrar - totalPorPagar) >= 0 ? clr.income : clr.expense}}>
                        {fmtTip(totalPorCobrar - totalPorPagar)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })()}
          </div>

        </div>
      )}

      {/* Overlay invisible para cerrar el popover al clicar fuera */}
      {popover && (
        <div onClick={()=>setPopover(null)}
             style={{position:"fixed",inset:0,zIndex:998}} />
      )}

      {/* Popover de detalle: importe/categoría clicada + botón para ir al historial */}
      {popover && (
        <div
          style={{
            position:"fixed",
            top: popover.y, left: popover.x,
            transform:"translate(-50%, -110%)",
            background:"var(--card)", border:"1px solid var(--border)",
            borderRadius:10, padding:"12px 14px", minWidth:180,
            boxShadow:"var(--shadow-md, 0 6px 20px rgba(0,0,0,.15))",
            zIndex:999,
          }}
          onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:12,color:"var(--gray-500)",marginBottom:4}}>{popover.title}</div>
          <div style={{fontSize:18,fontWeight:700,color:popover.color,marginBottom:10}}>{popover.value}</div>
          <button className="btn btn-primary btn-sm" style={{width:"100%",justifyContent:"center"}}
                  onClick={()=>goToFiltered(popover.filter, popover.path)}>
            {popover.path === "/inversiones"
              ? (lang==="en" ? "View in Investments →" : "Ver en Inversiones →")
              : popover.path === "/pendientes"
                ? (lang==="en" ? "View pending →" : "Ver pendientes →")
                : (lang==="en" ? "View history →" : "Ver historial →")}
          </button>
        </div>
      )}
    </div>
  );
}
